import { Injectable } from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  ValidatorFn,
} from '@angular/forms';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DPFormField } from '../models/DPFormField';
import { DPFormResult } from '../models/DPFormResult';
import { DPQuestion } from '../models/DPQuestion';
import { DPFormFieldType } from '../models/enums/DPFormFieldType';
import { DPValidationType } from '../models/enums/DPValidationType';
import { ResponsiveValue } from '../models/ResponsiveValue';
import { DPValidatorsService } from './dp-validators.service';
import { FormParserServiceInterface } from './form-parser.service.interface';

/**
 * Form parser service.
 *
 * This service will transform Question objects(from backend) to FormField objects.
 * Also based on the generated FormField objects, it will create Form object with validations on each field.
 */
@Injectable({
  providedIn: 'root',
})
export class FormParserService implements FormParserServiceInterface {
  constructor(private validators: DPValidatorsService) {}

  /**
   * Tranforms Question objects(from backend) to FormField objects. Question contains saved user answers and
   * those answers will be extracted to formValues object.
   *
   * @param questions Question objects.
   * @param parentField If Questions are sub questions, this field will point to the name of parent field.
   * @returns {Object} Object with form fields array and form values object.
   */
  public transformQuestions(questions: DPQuestion[], parentField?: string) {
    let formFields: DPFormField[] = [];
    let formValues: DPFormResult = {};

    // Sort questions by sortOrder in ascending order
    questions.sort((a, b) => a.sortOrder - b.sortOrder);

    questions.forEach((q) => {
      // Sort options by sortOrder in ascending order. Some questions such as
      // Dropdown, Checkbox, Radio, etc have options and they need to be sorted by sortOrder.
      q.options.sort((a, b) => a.sortOrder - b.sortOrder);
      const colSpan = this.getResponsiveValue<string>(q.layout);

      /**
       * Map Question to FormField.
       *
       * columnVisibility decides where this field should show in mobile, tablet, desktop screen sizes.
       * eg. desktop:true,mobile:false,tablet:false
       */
      let newField: DPFormField = {
        type: q.questionTypeID,
        label: q.text,
        name:
          q.questionTypeID !== DPFormFieldType.TextBlock
            ? `${q.pageQuestionID}`
            : '',
        required: q.required,
        options: q.options.map((opt) => ({
          key: opt.questionOptionID,
          value: opt.text,
        })),
        colSpan: {
          desktop: +colSpan['desktop'],
          tablet: +colSpan['tablet'],
          mobile: +colSpan['mobile'],
        },
        validations: q.validations,
        inputPlaceholder: q.inputPlaceholder,
        inputType: q.inputType,
        columnVisibility: this.getResponsiveValue<boolean>(
          q.columnVisibility,
          (val) => val !== 'false'
        ),
        parentField,
        subQuestionTriggerOptionId: q.subQuestionTriggerOptionId,
      };

      formFields.push(newField);

      /**
       * If the Question has sub questions, they need to be converted too.
       */
      if (q.subQuestions && q.subQuestions.length > 0) {
        const { formFields: subFields, formValues: subFormValues } =
          this.transformQuestions(q.subQuestions, newField.name);

        /**
         * If the question is Multi-value question or Yes/No question,
         * sub questions will be converted to subFields.
         * Otherwise, they need to be flattened.
         *
         * TODO: Need to review what are the cases to flatten subfields.
         */
        if (
          q.questionTypeID === DPFormFieldType.Multivalue ||
          q.questionTypeID === DPFormFieldType.YesNo
        ) {
          newField.subFields = subFields;
        } else {
          formFields.push(...subFields);
          formValues = {
            ...formValues,
            ...subFormValues,
          };
        }
      }

      // Extract value of question to formValues
      if (newField.name && q.userAnswer !== undefined && q.userAnswer[0]) {
        formValues[q.pageQuestionID] = q.userAnswer[0].value;
      }

      /**
       * This is used for layout of a form. If a field has newrow = True in colSpan,
       * that means next field should start in a new row.
       * This is done by adding an empty field in the row.
       */
      if (colSpan['newrow'] === 'True') {
        formFields.push({
          type: DPFormFieldType.None,
          label: '',
          name: '',
          colSpan: this.calcNewRowSpans(formFields),
        });
      }
    });

    return { formFields, formValues };
  }

  /**
   * This method creates a Form object from form field array.
   *
   * @param fields Array of form field objects
   * @param formValues Form values object. This will be used as initial values of form fields.
   * @param shouldAddValidators If true, set validators to fields
   * @returns Angular Reactive Form Object
   */
  public createDynamicForm(
    fields: DPFormField[],
    formValues: DPFormResult,
    shouldAddValidators?: boolean
  ) {
    // Form controls to create form
    const formFields: Record<string, UntypedFormControl> = {};

    fields.forEach((field) => {
      /**
       * If form name is empty, it means the field is Dummy field.
       */
      if (field.name) {
        formFields[field.name] = new UntypedFormControl(
          formValues[field.name] ?? ''
        );
      }
    });

    const form = new UntypedFormGroup(formFields);

    if (shouldAddValidators) {
      this.setValidators(fields, form);
    }

    return form;
  }

  /**
   * Set validators to form fields
   *
   * @param fields Array of form field objects
   * @param form Angular Reactive Form object.
   * @param unsubscriber$ Observable to unsubscribe value changes event.
   * @param additionalValidations Additional validation objects for form fields.
   */
  public setValidators(
    fields: DPFormField[],
    form: UntypedFormGroup,
    unsubscriber$?: Observable<unknown>,
    additionalValidations?: Record<string, ValidatorFn[]>
  ) {
    /**
     * When some fields change, certain fields need to updated or visibility of them changes.
     * This object represents which fields trigger updates of which other fields.
     */
    const triggerFields: Record<string, string> = {};

    fields.forEach((field) => {
      /**
       * If form name is empty, it means the field is Dummy field.
       */
      if (field.name) {
        // Angular validators of the field.
        const validators: ValidatorFn[] =
          (additionalValidations && additionalValidations[field.name]) || [];

        // If field is required,
        if (field.required) {
          // Address field has custom validator to check if the field is empty or not.
          if (field.type === DPFormFieldType.Address) {
            validators.push(this.validators.addressRequired(field));
          } else {
            validators.push(this.validators.required(field));
          }
        }

        // If the field is Yes/No field, it requires subFields to be required.
        if (field.type === DPFormFieldType.YesNo) {
          validators.push(this.validators.yesnoSubFieldRequired(field));
        }

        // If validations exist in the field
        if (field.validations?.length > 0) {
          field.validations.forEach((validation) => {
            /**
             * Check DPValidatorService for reference of the validation type
             */
            switch (validation.validationTypeId) {
              case DPValidationType.RegEx: {
                validators.push(this.validators.pattern(field, validation));
                break;
              }
              case DPValidationType.MinLength: {
                validators.push(this.validators.minLength(field, validation));
                break;
              }
              case DPValidationType.MaxLength: {
                validators.push(this.validators.maxLength(field, validation));
                break;
              }
              case DPValidationType.DurationMonthTotal: {
                validators.push(
                  this.validators.durationMonthTotal(field, validation)
                );
                break;
              }
              case DPValidationType.StartEndDateTotal:
                validators.push(
                  this.validators.startEndDateTotal(field, validation)
                );
                break;
              case DPValidationType.InvalidateFutureDate:
                validators.push(
                  this.validators.invalidateFutureDate(field, validation)
                );
                break;
              case DPValidationType.InvalidatePastDate:
                validators.push(
                  this.validators.invalidatePastDate(field, validation)
                );
                break;
              case DPValidationType.DateOfBirthPlus:
                validators.push(
                  this.validators.dateOfBirthPlus(field, validation)
                );
                break;
              case DPValidationType.CurrentResidence:
                validators.push(
                  this.validators.currentResidence(field, validation)
                );
                break;
              case DPValidationType.StartEndDateGap:
                validators.push(
                  this.validators.startEndDateGap(field, validation)
                );
                break;
              case DPValidationType.DriverLicence: {
                validators.push(
                  this.validators.driverLicense(field, validation)
                );
                if (validation.pageQuestionLookupId) {
                  triggerFields[`${validation.pageQuestionLookupId}`] =
                    field.name;
                }
                break;
              }
              case DPValidationType.DateYearLimit:
                validators.push(
                  this.validators.dateYearLimit(field, validation)
                );
                break;
              case DPValidationType.MinDateYearLimit:
                validators.push(
                  this.validators.minDateYearLimit(field, validation)
                );
              case DPValidationType.EmploymentResponseConflict:
                validators.push(this.validators.noPendingConflicts());
                break;
              case DPValidationType.MinStartDate:
                validators.push(
                  this.validators.minStartDate(field, validation)
                );
                break;
              case DPValidationType.MaxEndDate:
                validators.push(this.validators.maxEndDate(field, validation));
                break;
              default:
                break;
            }
          });
        }

        form.get(field.name).setValidators(validators);
      }
    });

    /**
     * Trigger fields only work if unsubscriber$ observable is provided.
     * Otherwise, there would be memory leak.
     */
    if (unsubscriber$) {
      for (const trigger in triggerFields) {
        /**
         * trigger: Name of the field to trigger updates
         * triggered: Name of the field to be updated.
         */
        const triggerField = form.get(trigger);
        const triggeredField = form.get(triggerFields[trigger]);
        if (triggerField && triggeredField) {
          triggerField.valueChanges
            .pipe(takeUntil(unsubscriber$))
            .subscribe(() => {
              triggeredField.updateValueAndValidity();
            });
        }
      }
    }
  }

  /**
   * Convert string to key/value object.
   *
   * @param strVal Value to convert. Format: "key1:value1,key2:value2,..."
   * @param convert Function to convert value to a specific type
   */
  private getResponsiveValue<T>(
    strVal: string,
    convert?: (val: string) => T
  ): Record<string, T> {
    if (!strVal) {
      return {};
    }

    return strVal
      .split(',')
      .reduce((result: ResponsiveValue<T>, currentItem: string) => {
        const [key, value] = currentItem.split(':');

        return {
          ...result,
          [key]: convert ? convert(value) : value,
        };
      }, {});
  }

  /**
   * Calculate remaining col span of a row.
   * This will be used for Dummy field to span rest of the row.
   *
   * @param fields Fields in a row
   * @returns Col span object
   */
  private calcNewRowSpans(fields: DPFormField[]) {
    let spans = {
      desktop: 0,
      tablet: 0,
      mobile: 0,
    };
    fields.forEach((f) => {
      spans.desktop += +f.colSpan.desktop;
      spans.tablet += +f.colSpan.tablet;
      spans.mobile += +f.colSpan.mobile;
    });

    spans.desktop = this.calcRemainingSpan(spans.desktop);
    spans.tablet = this.calcRemainingSpan(spans.tablet);
    spans.mobile = this.calcRemainingSpan(spans.mobile);

    return spans;
  }

  /**
   * Consider a row spans 12 columns, it calculates remaining cols.
   */
  private calcRemainingSpan(n: number) {
    n = n % 12;
    return n > 0 ? 12 - n : 0;
  }
}
